import React, { Fragment, useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, showConfirm, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─── API config ──────────────────────────────────────────────────
const BASE_URL = WEBSERVICE;
const getMerchantId = () =>
    localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";

const authHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${getToken()}`,
    "UUID": getAuthUuid(),

});

const api = {
    fetchAll: (merchantId) =>
        fetch(`${BASE_URL}/api/merchants/${merchantId}/shipping-profiles`, {
            headers: authHeaders(),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }),

    create: (merchantId, payload) =>
        fetch(`${BASE_URL}/api/merchants/${merchantId}/shipping-profiles`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(payload),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }), 

    update: (merchantId, id, payload) =>
        fetch(`${BASE_URL}/api/merchants/${merchantId}/shipping-profiles/update/${id}`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(payload),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }),

    remove: (merchantId, id) =>
        fetch(`${BASE_URL}/api/merchants/${merchantId}/shipping-profiles/remove/${id}`, {
            method: "POST",
            headers: authHeaders(),
        }).then(r => {
            if (!r.ok) throw { response: { status: r.status } };
            return r.json();
        }),
};

// ─── Map API record → UI profile ─────────────────────────────────
function mapRecord(rec) {
    return {
        id: rec.id,
        name: rec.profileName,
        shippingHandling: String(rec.handlingFeeValue ?? ""),
        taxRate: String(rec.taxRate ?? ""),
        isDefaultProfile: rec.isDefaultProfile ?? 0,
        isActive: rec.isActive ?? 1,
        createdAt: rec.createdAt,
    };
}

// ─── Styles ──────────────────────────────────────────────────────
const S = {
    pageWrapper: { padding: "36px 24px 60px" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", flexWrap: "wrap", gap: "16px" },
    pageTitle: { fontSize: "24px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
    card: { background: "var(--body-color-less)", borderRadius: "16px", border: "1px solid var(--body-color-less2)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "24px" },
    cardHeader: { display: "flex", alignItems: "center", gap: "12px", padding: "20px 28px", borderBottom: "1px solid var(--body-color-less2)" },
    cardHeaderIcon: { width: "36px", height: "36px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" },
    cardHeaderTitle: { fontSize: "16px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    cardBody: { padding: "24px 28px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
    label: { fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--text-color)", opacity: 0.7 },
    input: { padding: "10px 14px", fontSize: "14px", borderRadius: "10px", border: "1px solid var(--body-color-less2)", background: "var(--body-color)", color: "var(--text-color)", outline: "none", width: "100%", boxSizing: "border-box" },
    inputError: { padding: "10px 14px", fontSize: "14px", borderRadius: "10px", border: "1px solid #ef4444", background: "var(--body-color)", color: "var(--text-color)", outline: "none", width: "100%", boxSizing: "border-box" },
    btnPrimary: { padding: "10px 24px", background: "var(--btn-color)", color: "#fff", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
    btnOutline: { padding: "8px 18px", background: "transparent", border: "1px solid var(--btn-color)", color: "var(--btn-color)", borderRadius: "10px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
    btnDanger: { padding: "9px 14px", background: "rgba(239,68,68,0.1)", border: "none", color: "#ef4444", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },
    errorText: { fontSize: "11px", color: "#ef4444", marginTop: "2px" },
    emptyState: { textAlign: "center", padding: "52px 24px" },
};

const EMPTY_PROFILE = {
    id: null,
    name: "",
    shippingHandling: "",
    taxRate: "",
    isDefaultProfile: 0,
};

// ─── Profile form ─────────────────────────────────────────────────
function ProfileForm({ initial, onSave, onCancel, saving }) {
    const [form, setForm] = useState(initial || EMPTY_PROFILE);
    const [errors, setErrors] = useState({});
    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const validatePercent = (val, fieldLabel) => {
        if (val === "" || val === null || val === undefined) return `${fieldLabel} is required`;
        const num = parseFloat(val);
        if (isNaN(num)) return "Enter a valid number";
        if (num < 0 || num > 100) return "Value must be between 0% and 100%";
        if (!/^\d+(\.\d{1,2})?$/.test(String(val).trim())) return "Maximum 2 decimal places allowed";
        return null;
    };

    const validate = () => {
        const e = {};
        const trimmedName = form.name.trim();
        if (!trimmedName) {
            e.name = "Profile name is required";
        } else if (trimmedName.length < 2 || trimmedName.length > 100) {
            e.name = "Profile name must be 2–100 characters";
        } else if (!/^[a-zA-Z0-9 \-&]+$/.test(trimmedName)) {
            e.name = "Only letters, numbers, spaces, hyphens, and & are allowed";
        }
        const shippingErr = validatePercent(form.shippingHandling, "Shipping (%)");
        if (shippingErr) e.shippingHandling = shippingErr;
        const taxErr = validatePercent(form.taxRate, "Tax Rate (%)");
        if (taxErr) e.taxRate = taxErr;
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* Profile Name */}
            <div style={S.formGroup}>
                <label style={S.label}>Profile Name *</label>
                <input
                    style={errors.name ? S.inputError : S.input}
                    value={form.name}
                    onChange={e => set("name", e.target.value)}
                    placeholder="e.g. Standard Rates"
                />
                {errors.name && <span style={S.errorText}>{errors.name}</span>}
            </div>

            {/* Two rate fields side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>

                {/* Shipping */}
                <div style={S.formGroup}>
                    <label style={S.label}>Shipping (%) *</label>
                    <div style={{ position: "relative" }}>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={{ ...(errors.shippingHandling ? S.inputError : S.input), paddingLeft: "15px", paddingRight: "36px" }}
                            value={form.shippingHandling}
                            onChange={e => set("shippingHandling", e.target.value)}
                            placeholder="0.00"
                        />
                        <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", fontWeight: "700", color: "var(--text-color)", opacity: 0.4, pointerEvents: "none" }}>%</span>
                    </div>
                    {errors.shippingHandling
                        ? <span style={S.errorText}>{errors.shippingHandling}</span>
                        : <span style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.45, marginTop: "2px" }}>Percentage added on top of the subtotal for shipping</span>
                    }
                </div>

                {/* Tax Rate */}
                <div style={S.formGroup}>
                    <label style={S.label}>Tax Rate (%) *</label>
                    <div style={{ position: "relative" }}>
                        <i className="mdi mdi-receipt-text" style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", color: "var(--text-color)", opacity: 0.35, pointerEvents: "none" }} />
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={{ ...(errors.taxRate ? S.inputError : S.input), paddingLeft: "15px", paddingRight: "36px" }}
                            value={form.taxRate}
                            onChange={e => set("taxRate", e.target.value)}
                            placeholder="0.00"
                        />
                        <span style={{ position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", fontWeight: "700", color: "var(--text-color)", opacity: 0.4, pointerEvents: "none" }}>%</span>
                    </div>
                    {errors.taxRate
                        ? <span style={S.errorText}>{errors.taxRate}</span>
                        : <span style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.45, marginTop: "2px" }}>Tax percentage applied to the order total</span>
                    }
                </div>
            </div>

            {/* Default Profile Toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 18px", borderRadius: "12px", background: "var(--body-color)", border: "1px solid var(--body-color-less2)" }}>
                <div
                    onClick={() => set("isDefaultProfile", form.isDefaultProfile === 1 ? 0 : 1)}
                    style={{
                        width: "42px", height: "24px", borderRadius: "12px", cursor: "pointer",
                        background: form.isDefaultProfile === 1 ? "var(--btn-color)" : "var(--body-color-less2)",
                        position: "relative", transition: "background 0.2s", flexShrink: 0,
                    }}
                >
                    <div style={{
                        position: "absolute", top: "3px",
                        left: form.isDefaultProfile === 1 ? "21px" : "3px",
                        width: "18px", height: "18px", borderRadius: "50%",
                        background: "#fff", transition: "left 0.2s",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    }} />
                </div>
                <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)" }}>Set as Default Profile</div>
                    <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px" }}>
                        This profile will be pre-selected at checkout
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "4px" }}>
                <button type="button" onClick={onCancel} style={S.btnOutline} disabled={saving}>Cancel</button>
                <button
                    type="button"
                    onClick={() => { if (validate()) onSave(form); }}
                    style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
                    disabled={saving}
                >
                    {saving
                        ? <><i className="mdi mdi-loading mdi-spin" /> Saving…</>
                        : <><i className="mdi mdi-check" /> Save Profile</>
                    }
                </button>
            </div>
        </div>
    );
}

// ─── Root component ───────────────────────────────────────────────
export default function ShippingHandling() {
    const merchantId = getMerchantId();
    const isReadOnly = currentPageAccess() === "READ";

    const [profiles, setProfiles] = useState([]);
    const [mode, setMode] = useState("list");   // list | add | edit
    const [editing, setEditing] = useState(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // ADD this right after: const [deletingId, setDeletingId] = useState(null);

    // AFTER
    const loadProfiles = () => {
        setLoading(true);
        api.fetchAll(merchantId)
            .then(res => {
                if (res.error !== 0) {
                    showToast("error", res.message || "Failed to load profiles.");
                    return;
                }
                setProfiles((res.data || []).map(mapRecord));
            })
            .catch(err => showHttpError(err))
            .finally(() => setLoading(false));
    };

    // ── Fetch on mount ──
    // AFTER
    useEffect(() => {
        loadProfiles();
    }, [merchantId]);

    // ── Save ──
    const handleSave = async (form) => {
        if (isReadOnly) {
            showToast("error", "You need write access to perform this operation");
            return;
        }
        setSaving(true);
        const payload = {
            name: form.name,
            shippingHandling: parseFloat(form.shippingHandling),
            taxRate: parseFloat(form.taxRate),
            isDefaultProfile: form.isDefaultProfile,
        };

        try {
            let res;
            if (editing) {
                res = await api.update(merchantId, editing.id, payload);
            } else {
                res = await api.create(merchantId, payload);
            }

            if (res.error !== "0") {
                showToast("error", res.message || "Failed to save profile.");
                return;
            }

            const saved = mapRecord(res.data);

            setProfiles(prev =>
                editing
                    ? prev.map(p => p.id === saved.id ? saved : p)
                    : [...prev, saved]
            );

            showToast("success", res.message || (editing ? "Profile updated successfully." : "Profile created successfully."));
            setMode("list");
            setEditing(null);
            loadProfiles();
        } catch (err) {
            showHttpError(err);
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ──
    const handleDelete = (profile) => {
        if (isReadOnly) {
            showToast("error", "You need write access to perform this operation");
            return;
        }
        showConfirm(
            "Delete Profile",
            `Are you sure you want to delete <strong>${profile.name}</strong>? This action cannot be undone.`,
            "warning",
            async () => {
                setDeletingId(profile.id);
                try {
                    const res = await api.remove(merchantId, profile.id);
                    if (res.error === 1) {
                        showToast("error", res.message || "Failed to delete profile.");
                        return;
                    }
                    setProfiles(prev => prev.filter(p => p.id !== profile.id));
                    showToast("success", `Profile "${profile.name}" deleted.`);
                    loadProfiles();
                } catch (err) {
                    showHttpError(err);
                } finally {
                    setDeletingId(null);
                }
            }
        );
    };
    return (
        <Fragment>
            <div className="container-scroller">
                <Sidebar />
                <Header />
                <div className="main-panel">
                    <div style={S.pageWrapper}>

                        {/* Page Header */}
                        <div style={S.headerRow}>
                            <div>
                                {(mode === "add" || mode === "edit") && (
                                    <span onClick={() => { setMode("list"); setEditing(null); }}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--btn-color)", cursor: "pointer", marginBottom: 8, userSelect: "none", opacity: .85, transition: "opacity .15s" }}
                                        onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.textDecoration = "underline"; }}
                                        onMouseLeave={e => { e.currentTarget.style.opacity = .85; e.currentTarget.style.textDecoration = "none"; }}>
                                        <i className="mdi mdi-arrow-left" style={{ fontSize: 14 }} />
                                        Back to Shipping
                                    </span>
                                )}
                                <h2 style={S.pageTitle}>
                                    {mode === "add" ? "Create Shipping Profile"
                                        : mode === "edit" ? `Edit — ${editing?.name}`
                                        : "Shipping"}
                                </h2>
                                {mode === "list" && <div style={S.pageSubtitle}>Define shipping and tax rate profiles for your checkout</div>}
                            </div>
                            {mode === "list" && !isReadOnly && (
                                <button style={S.btnPrimary} onClick={() => { setEditing(null); setMode("add"); }}>
                                    <i className="mdi mdi-plus" /> Add Profile
                                </button>
                            )}
                        </div>

                        {/* Loading */}
                        {loading && (
                            <div style={{ ...S.card, padding: "40px", textAlign: "center" }}>
                                <i className="mdi mdi-loading mdi-spin" style={{ fontSize: "28px", color: "var(--btn-color)", display: "block", marginBottom: "12px" }} />
                                <div style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.5 }}>Loading shipping profiles…</div>
                            </div>
                        )}

                        {/* Stats — list mode only */}
                        {!loading && mode === "list" && profiles.length > 0 && (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "24px" }}>
                                {[
                                    { label: "Total Profiles", value: profiles.length, icon: "mdi-format-list-bulleted-type", color: "var(--btn-color)" },
                                    { label: "Avg. Shipping Rate", value: `${(profiles.reduce((a, p) => a + parseFloat(p.shippingHandling || 0), 0) / profiles.length).toFixed(1)}%`, icon: "mdi-truck-fast", color: "#3b82f6" },
                                    { label: "Avg. Tax Rate", value: `${(profiles.reduce((a, p) => a + parseFloat(p.taxRate || 0), 0) / profiles.length).toFixed(1)}%`, icon: "mdi-receipt", color: "#f59e0b" },
                                ].map(stat => (
                                    <div key={stat.label} style={{ background: "var(--body-color-less)", borderRadius: "14px", border: "1px solid var(--body-color-less2)", padding: "18px 20px", display: "flex", alignItems: "center", gap: "14px" }}>
                                        <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: `color-mix(in srgb, ${stat.color} 12%, transparent)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", color: stat.color, flexShrink: 0 }}>
                                            <i className={`mdi ${stat.icon}`} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: "22px", fontWeight: "700", color: "var(--text-color)", lineHeight: 1 }}>{stat.value}</div>
                                            <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, marginTop: "3px" }}>{stat.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Form Card */}
                        {!loading && (mode === "add" || mode === "edit") && (
                            <div style={S.card}>
                                <div style={S.cardHeader}>
                                    <div style={{ ...S.cardHeaderIcon, background: "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 15%, transparent), color-mix(in srgb, var(--btn-color) 5%, transparent))", color: "var(--btn-color)" }}>
                                        <i className={mode === "add" ? "mdi mdi-plus" : "mdi mdi-pencil"} />
                                    </div>
                                    <h4 style={S.cardHeaderTitle}>
                                        {mode === "add" ? "New Shipping Profile" : `Edit — ${editing?.name}`}
                                    </h4>
                                </div>
                                <div style={S.cardBody}>
                                    <ProfileForm
                                        initial={editing}
                                        onSave={handleSave}
                                        onCancel={() => { setMode("list"); setEditing(null); }}
                                        saving={saving}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Profile List */}
                        {!loading && mode === "list" && profiles.length > 0 && (
                            <div style={S.card}>
                                <div style={S.cardHeader}>
                                    <div style={{ ...S.cardHeaderIcon, background: "linear-gradient(135deg, color-mix(in srgb, #3b82f6 15%, transparent), color-mix(in srgb, #3b82f6 5%, transparent))", color: "#3b82f6" }}>
                                        <i className="mdi mdi-view-list" />
                                    </div>
                                    <h4 style={S.cardHeaderTitle}>Profiles</h4>
                                    <span style={{ marginLeft: "auto", fontSize: "12px", fontWeight: "600", color: "var(--text-color)", opacity: 0.4 }}>
                                        {profiles.length} profile{profiles.length !== 1 ? "s" : ""}
                                    </span>
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                {["Profile Name", "Shipping", "Tax Rate", "Default", ""].map(h => (
                                                    <th key={h} style={{ padding: "12px 24px", fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-color)", opacity: 0.5, borderBottom: "1px solid var(--body-color-less2)", textAlign: h === "" ? "right" : "left" }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map(p => {
                                                const isDeleting = deletingId === p.id;
                                                return (
                                                    <tr key={p.id} style={{ opacity: isDeleting ? 0.5 : 1, transition: "opacity 0.2s" }}>

                                                        {/* Name */}
                                                        <td style={{ padding: "14px 24px", fontSize: "14px", fontWeight: "600", color: "var(--text-color)", borderBottom: "1px solid var(--body-color-less2)", verticalAlign: "middle" }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                                <div style={{ width: "32px", height: "32px", borderRadius: "9px", background: "color-mix(in srgb, var(--btn-color) 10%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--btn-color)", fontSize: "14px", flexShrink: 0 }}>
                                                                    <i className="mdi mdi-truck-delivery" />
                                                                </div>
                                                                {p.name}
                                                            </div>
                                                        </td>

                                                        {/* Shipping */}
                                                        <td style={{ padding: "14px 24px", borderBottom: "1px solid var(--body-color-less2)", verticalAlign: "middle" }}>
                                                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "5px 14px", borderRadius: "20px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}>
                                                                <i className="mdi mdi-truck" style={{ fontSize: "13px", color: "#3b82f6" }} />
                                                                <span style={{ fontSize: "14px", fontWeight: "700", color: "#3b82f6", fontFamily: "'SF Mono','Fira Code',monospace" }}>
                                                                    {parseFloat(p.shippingHandling).toFixed(2)}%
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Tax Rate */}
                                                        <td style={{ padding: "14px 24px", borderBottom: "1px solid var(--body-color-less2)", verticalAlign: "middle" }}>
                                                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "5px 14px", borderRadius: "20px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}>
                                                                <i className="mdi mdi-receipt-text" style={{ fontSize: "13px", color: "#f59e0b" }} />
                                                                <span style={{ fontSize: "14px", fontWeight: "700", color: "#f59e0b", fontFamily: "'SF Mono','Fira Code',monospace" }}>
                                                                    {parseFloat(p.taxRate).toFixed(2)}%
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Default badge */}
                                                        <td style={{ padding: "14px 24px", borderBottom: "1px solid var(--body-color-less2)", verticalAlign: "middle" }}>
                                                            {p.isDefaultProfile === 1 ? (
                                                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "20px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)" }}>
                                                                    <i className="mdi mdi-check-circle" style={{ fontSize: "13px", color: "#22c55e" }} />
                                                                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#22c55e" }}>Default</span>
                                                                </div>
                                                            ) : (
                                                                <span style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.3 }}>—</span>
                                                            )}
                                                        </td>

                                                        {/* Actions */}
                                                        <td style={{ padding: "14px 24px", borderBottom: "1px solid var(--body-color-less2)", verticalAlign: "middle", textAlign: "right" }}>
                                                            {isReadOnly ? (
                                                                <span style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.4 }}>—</span>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={() => { setEditing(p); setMode("edit"); }}
                                                                        style={{ ...S.btnOutline, marginRight: "8px" }}
                                                                        disabled={isDeleting}
                                                                    >
                                                                        <i className="mdi mdi-pencil" /> Edit
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDelete(p)}
                                                                        style={{ ...S.btnDanger, opacity: isDeleting ? 0.5 : 1, cursor: isDeleting ? "not-allowed" : "pointer" }}
                                                                        disabled={isDeleting}
                                                                    >
                                                                        {isDeleting
                                                                            ? <i className="mdi mdi-loading mdi-spin" style={{ fontSize: "14px" }} />
                                                                            : <i className="mdi mdi-delete" />
                                                                        }
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!loading && mode === "list" && profiles.length === 0 && (
                            <div style={S.card}>
                                <div style={S.emptyState}>
                                    <div style={{ width: "56px", height: "56px", borderRadius: "16px", background: "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 12%, transparent), color-mix(in srgb, var(--btn-color) 4%, transparent))", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "24px", color: "var(--btn-color)" }}>
                                        <i className="mdi mdi-truck-delivery" />
                                    </div>
                                    <div style={{ fontSize: "15px", fontWeight: "600", color: "var(--text-color)", marginBottom: "6px" }}>No profiles yet</div>
                                    <div style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.5 }}>Add a profile to define shipping and tax rates for your checkout</div>
                                    {!isReadOnly && (
                                        <button style={{ ...S.btnPrimary, marginTop: "18px" }} onClick={() => { setEditing(null); setMode("add"); }}>
                                            <i className="mdi mdi-plus" /> Add Shipping Profile
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                    <Footer />
                </div>
            </div>
        </Fragment>
    );
}